import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';
import { HttpRequestCreateFromTextInput } from '../../../components/Action/HttpRequestActionFromText/CreateHttpRequestAction.modal';
import { QueryKey } from '@/Config/api/queryKey';
import { pushGtmEvent } from '@/Config/libs/analytics';
import { HttpActionFromTextDto } from '@/Domain/api/Action/dto/Action.dto';
import { GtmEventEnum } from '@/Domain/enum/GtmEvent.enum';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import { useModalStore } from '@/Domain/store/modal.store';

const createHttpRequestActionSchema = z.object({
  action_description: z.string().min(1).max(500),
});

type CreateHttpRequestActionForm = z.infer<typeof createHttpRequestActionSchema>;

export const useCreateHttpRequestActionForm = () => {
  const { t } = useTranslation('http_request_ai_action', { keyPrefix: 'create_modal' });
  const { open, closeModal } = useModalStore();
  const { capture } = usePostHogAnalytics();

  const form = useForm<CreateHttpRequestActionForm>({
    mode: 'onChange',
    defaultValues: {
      action_description: '',
    },
    resolver: zodResolver(createHttpRequestActionSchema),
  });

  useEffect(() => {
    form.reset({
      action_description: '',
    });
  }, [open]);

  const createHttpRequestAIActionFromText = useMutation<
    HttpActionFromTextDto,
    undefined,
    HttpRequestCreateFromTextInput
  >({
    mutationKey: [QueryKey.actions.httpRequest.from_text],
    onError: (_error, variables, _context) => {
      toast.error(t('failed_toast'), { id: variables.process_id });
    },
  });

  const handleSubmit = (data: CreateHttpRequestActionForm) => {
    closeModal();
    const id = toast.loading(t('loading_toast'));
    createHttpRequestAIActionFromText.mutate({
      user_input: data.action_description,
      process_id: id,
    });
    capture(PostHogEventEnum.ActionCreated, { action_type: 'http_request' });
    pushGtmEvent(GtmEventEnum.ActionCreated, { action_type: 'http_request' });
  };

  return { form, handleSubmit };
};
